/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { postsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PostCard from '../components/Feed/PostCard';
import CreatePost from '../components/Feed/CreatePost';
import Stories from '../components/Stories/Stories';
import { Globe, TrendingUp, Loader, Hash } from 'lucide-react';
import '../components/Feed/Feed.css';

const TRENDING_TOPICS = [
  { tag: 'technology', count: '12.4K' },
  { tag: 'AI',         count: '9.8K'  },
  { tag: 'startup',    count: '7.2K'  },
  { tag: 'webdev',     count: '6.1K'  },
  { tag: 'design',     count: '4.9K'  },
  { tag: 'productivity',count: '4.2K' },
  { tag: 'opensource', count: '3.8K'  },
  { tag: 'buildinpublic',count:'3.1K' },
];

export default function FeedPage() {
  const { user }  = useAuth();
  const toast     = useToast();
  const [posts, setPosts]      = useState([]);
  const [loading, setLoading]  = useState(true);
  const [loadMore, setMore]    = useState(false);
  const [tab, setTab]          = useState('foryou');
  const [page, setPage]        = useState(1);
  const [hasMore, setHasMore]  = useState(true);
  const sentinelRef = useRef(null);

  const loadPosts = useCallback(async (t, p, append = false) => {
    if (p === 1) setLoading(true); else setMore(true);
    try {
      const { data } = t === 'foryou'
        ? await postsAPI.getFeed(p)
        : await postsAPI.getExplore();
      if (data.length < 20) setHasMore(false);
      setPosts(prev => append ? [...prev, ...data] : data);
    } catch { toast.error('Could not load posts'); }
    finally { setLoading(false); setMore(false); }
  }, []);

  useEffect(() => {
    setPage(1); setHasMore(true);
    loadPosts(tab, 1, false);
  }, [tab]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadMore && !loading) {
        const next = page + 1;
        setPage(next);
        loadPosts(tab, next, true);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading, page, tab]);

  const handleCreate = async (content) => {
    try {
      const { data } = await postsAPI.create({ content });
      setPosts(prev => [data, ...prev]);
      toast.success('Posted! 🎉');
    } catch { toast.error('Failed to post'); }
  };

  const handleLike     = async (id) => { try { const { data } = await postsAPI.like(id);     setPosts(p => p.map(x => (x._id||x.id)===id ? data : x)); } catch {} };
  const handleRepost   = async (id) => { try { const { data } = await postsAPI.repost(id);   setPosts(p => p.map(x => (x._id||x.id)===id ? data : x)); toast.success('Reposted!'); } catch {} };
  const handleBookmark = async (id) => { try { const { data } = await postsAPI.bookmark(id); setPosts(p => p.map(x => (x._id||x.id)===id ? data : x)); toast.success('Bookmarked!'); } catch {} };
  const handleDelete   = async (id) => { try { await postsAPI.delete(id); setPosts(p => p.filter(x => (x._id||x.id)!==id)); toast.info('Post deleted'); } catch {} };

  return (
    <div className="feed-layout">
      {/* Main feed */}
      <div className="feed-main">
        {/* Stories bar */}
        <Stories />

        {/* Tabs */}
        <div className="feed-tabs">
          {[
            { key:'foryou',   label:'For You',  icon:<Globe size={15}/> },
            { key:'trending', label:'Trending', icon:<TrendingUp size={15}/> },
            { key:'following',label:'Following',icon:<Hash size={15}/> },
          ].map(t => (
            <button key={t.key}
              className={`feed-tab ${tab===t.key?'active':''}`}
              onClick={() => setTab(t.key)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Create post */}
        <CreatePost user={user} onSubmit={handleCreate}/>

        {/* Posts */}
        <div className="posts-list">
          {loading ? (
            Array(4).fill(0).map((_,i) => <PostSkeleton key={i}/>)
          ) : posts.length === 0 ? (
            <div className="empty-state" style={{ paddingTop:60 }}>
              <Globe size={48}/>
              <h3>Nothing here yet</h3>
              <p>Be the first to post something!</p>
            </div>
          ) : posts.map(p => (
            <PostCard key={p._id||p.id} post={p} currentUser={user}
              onLike={handleLike} onRepost={handleRepost}
              onBookmark={handleBookmark} onDelete={handleDelete}/>
          ))}

          <div ref={sentinelRef} style={{ height:20 }}/>
          {loadMore && (
            <div style={{ display:'flex', justifyContent:'center', padding:20 }}>
              <Loader size={22} className="animate-spin" style={{ color:'var(--primary)' }}/>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <aside className="feed-sidebar">
        <div className="card" style={{ marginBottom:14 }}>
          <h3 style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:14, display:'flex', alignItems:'center', gap:6 }}>
            <TrendingUp size={16} style={{ color:'var(--primary-light)' }}/> Trending now
          </h3>
          {TRENDING_TOPICS.map((item, i) => (
            <div key={item.tag} className="trending-item"
              onClick={() => { setTab('trending'); toast.info(`Showing #${item.tag} posts`); }}>
              <div>
                <p style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Trending #{i+1}</p>
                <p className="trending-tag">#{item.tag}</p>
                <p className="trending-count">{item.count} posts</p>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <h3 style={{ fontWeight:700, fontSize:'0.9rem', marginBottom:8 }}>What is Nexa?</h3>
          <p style={{ fontSize:'0.82rem', color:'var(--text-secondary)', lineHeight:1.6 }}>
            Your global public town square. Share ideas, follow topics, and join conversations happening right now — across the world.
          </p>
        </div>
      </aside>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
      <div style={{ display:'flex', gap:12, marginBottom:10 }}>
        <div className="skeleton" style={{ width:44, height:44, borderRadius:'50%', flexShrink:0 }}/>
        <div style={{ flex:1 }}>
          <div className="skeleton" style={{ width:'35%', height:13, marginBottom:6 }}/>
          <div className="skeleton" style={{ width:'20%', height:11 }}/>
        </div>
      </div>
      <div className="skeleton" style={{ width:'95%', height:13, marginBottom:6 }}/>
      <div className="skeleton" style={{ width:'80%', height:13, marginBottom:6 }}/>
      <div className="skeleton" style={{ width:'55%', height:13 }}/>
    </div>
  );
}
